import {v4 as uuid} from 'uuid';

const BLOCK = 32 * 1024 * 1024;
const hex = (buffer: ArrayBuffer) => Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, '0')).join('');
const digest = async (data: ArrayBuffer) => hex(await crypto.subtle.digest('SHA-256', data));

// Hash the entire file with bounded memory. A chained block digest also catches
// modifications in the middle of a reselected file; names/mtime alone cannot.
export async function fingerprint(file: File, check: () => void, progress: (n: number) => void): Promise<string> {
    let chain = await digest(new TextEncoder().encode(JSON.stringify([file.name, file.size, file.lastModified])).buffer);
    for (let start = 0; start < file.size; start += BLOCK) {
        check();
        const block = await digest(await file.slice(start, start + BLOCK).arrayBuffer());
        chain = await digest(new TextEncoder().encode(chain + block).buffer);
        progress(Math.min(start + BLOCK, file.size));
    }
    return chain;
}

export async function uploadVod(api: any, params: any): Promise<any> {
    try { return await transferVod(api, params); }
    finally {
        const file = params.file;
        if (file.discardUpload && file.vodUploadSessionId) {
            try {
                await api.post('tracks/uploads/' + file.vodUploadSessionId + '/cancel');
                localStorage.removeItem(file.vodUploadStorageKey);
            } catch { /* Session stays resumable if the server cannot confirm cancellation. */ }
        }
    }
}

async function transferVod(api: any, params: any): Promise<any> {
    const file = params.file;
    const check = () => params.cancelToken?.throwIfRequested();
    const report = (loaded: number, phase = 'uploading') => {
        file.uploadPhase = phase;
        params.onProgress?.({loaded, total: file.size});
    };
    const hash = await fingerprint(file, check, n => report(n, 'checking'));
    check();
    // No tokens or server credentials are stored here. Session ownership is
    // checked by MW on every operation, using the normal refreshed access token.
    const owner = JSON.parse(localStorage.getItem('userData') || '{}');
    const data = params.postParams;
    const key = 'vod-upload-v1:' + await digest(new TextEncoder().encode(JSON.stringify([
        owner.id, owner.operatorId, data.contentId, data.pluginInstanceId, data.props?.profile, hash,
    ])).buffer);
    const id = localStorage.getItem(key) || uuid();
    localStorage.setItem(key, id); // Before POST: retry of a lost creation reply is idempotent.
    file.vodUploadSessionId = id;
    file.vodUploadStorageKey = key;
    const route = 'tracks/uploads/' + id;
    let session = await api.post('tracks/uploads', {id, filename: file.name, size: file.size, fingerprint: hash,
        contentId: data.contentId, pluginInstanceId: data.pluginInstanceId, props: data.props});
    if (session.state === 'cancelled') { localStorage.removeItem(key); throw {message: 'Upload cancelled. Select the file again.'}; }
    if (session.state !== 'completed' && Date.parse(session.expiresAt) <= Date.now()) throw {message: 'Upload expired. Cancel it before starting again.'};
    let failures = 0;
    while (session.state !== 'completed' && session.offset < file.size) {
        check();
        const offset = session.offset;
        if (!Number.isSafeInteger(offset) || offset < 0 || offset > file.size) throw new Error('Invalid server upload offset');
        const blob = file.slice(offset, offset + Math.min(session.chunkSize, BLOCK));
        const sha256 = await digest(await blob.arrayBuffer());
        check();
        try {
            session = await api.upload(route + '/chunks', {file: new File([blob], 'chunk.bin'), cancelToken: params.cancelToken,
                postParams: {offset, sha256}, onProgress: (e: any) => report(offset + Math.min(blob.size, e.loaded))});
            failures = 0;
            report(session.offset);
        } catch (error) {
            check();
            // A lost response may hide a committed chunk. Always recover the
            // authoritative offset before retrying; never append blindly.
            if (error.retryable === false || (++failures > 8)) throw error;
            report(offset, 'reconnecting');
            await new Promise(resolve => setTimeout(resolve, Math.min(1000 * 2 ** failures, 15000)));
            check();
            for (;;) {
                check();
                try { session = await api.get(route); break; }
                catch (statusError) {
                    if (statusError.retryable === false || ++failures > 8) throw statusError;
                    await new Promise(resolve => setTimeout(resolve, 5000));
                }
            }
            if (session.state === 'cancelled') throw {message: 'Upload cancelled'};
        }
    }
    check();
    report(file.size, 'finalizing');
    let result;
    for (let attempt = 0; ; attempt++) {
        check();
        try { result = await api.post(route + '/complete'); break; }
        catch (error) {
            if (error.retryable === false || attempt >= 7) throw error;
            await new Promise(resolve => setTimeout(resolve, 3000));
        }
    }
    localStorage.removeItem(key);
    return result;
}
